/**
 * @fileoverview Cart System Utilities
 * Common utility functions for performance, validation, and helpers
 */

import { DEBUG, PERFORMANCE_THRESHOLDS } from '../constants/index.js';
import { TypeCheckers } from '../types/index.js';
import { cartPerformance, CartPerformance } from './Performance.js';
import { smartUpdater, SmartUpdater } from '../ui/SmartUpdater.js';

/**
 * Performance utilities
 */
export const Performance = {
  /**
   * Measure execution time of a function
   * @param {Function} fn - Function to measure
   * @param {string} label - Label for the measurement
   * @returns {Promise<any>} Function result
   */
  async measure(fn, label = 'operation') {
    const start = performance.now();
    const result = await fn();
    const end = performance.now();
    const duration = end - start;
    
    this.logPerformance(label, duration);
    return result;
  },

  /**
   * Log performance metrics
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in milliseconds
   */
  logPerformance(operation, duration) {
    const level = this.getPerformanceLevel(duration);
    const message = `${DEBUG.PERFORMANCE_LOG_PREFIX} ${operation}: ${duration.toFixed(2)}ms (${level})`;
    
    switch (level) {
      case 'slow':
        console.warn(message);
        break;
      case 'acceptable':
        console.info(message);
        break;
      default:
        console.log(message);
    }
  },

  /**
   * Determine performance level based on duration
   * @param {number} duration - Duration in milliseconds
   * @returns {string} Performance level
   */
  getPerformanceLevel(duration) {
    if (duration > PERFORMANCE_THRESHOLDS.SLOW_LOAD) return 'slow';
    if (duration > PERFORMANCE_THRESHOLDS.ACCEPTABLE_LOAD) return 'acceptable';
    return 'fast';
  },

  /**
   * Debounce function calls
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  /**
   * Throttle function calls
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @returns {Function} Throttled function
   */
  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  /**
   * Batch DOM updates using requestAnimationFrame
   * @param {Function} updateFn - Function that performs DOM updates
   * @returns {Promise<void>}
   */
  batchDOMUpdates(updateFn) {
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        updateFn();
        resolve();
      });
    });
  }
};

/**
 * DOM utilities
 */
export const DOM = {
  /**
   * Query selector with error handling
   * @param {string} selector - CSS selector
   * @param {Element} [context=document] - Context element
   * @returns {Element|null} Found element
   */
  query(selector, context = document) {
    try {
      return context.querySelector(selector);
    } catch (error) {
      Debug.log('error', `${DEBUG.ERROR_LOG_PREFIX} Invalid selector: ${selector}`, error);
      return null;
    }
  },

  /**
   * Query all with error handling
   * @param {string} selector - CSS selector
   * @param {Element} [context=document] - Context element
   * @returns {NodeList} Found elements
   */
  queryAll(selector, context = document) {
    try {
      return context.querySelectorAll(selector);
    } catch (error) {
      Debug.log('error', `${DEBUG.ERROR_LOG_PREFIX} Invalid selector: ${selector}`, error);
      return [];
    }
  },

  /**
   * Add event listener with error handling and sandbox support
   * @param {Element} element - Target element
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   * @param {Object} [options] - Event options
   */
  on(element, event, handler, options = {}) {
    if (!element || typeof handler !== 'function') {
      Debug.log('error', `${DEBUG.ERROR_LOG_PREFIX} Invalid event listener parameters`, {
        hasElement: !!element,
        handlerType: typeof handler,
        event
      });
      return;
    }
    
    try {
      // Create a wrapped handler that catches errors
      const wrappedHandler = (e) => {
        try {
          // Check if we're in a sandboxed environment
          const isSandboxed = window.origin === 'null' || 
            window.location.origin === 'null' || 
            !window.origin;

          if (isSandboxed) {
            Debug.log('info', 'Handling event in sandboxed environment', {
              event,
              elementId: element.id,
              elementType: element.tagName
            });
          }

          // Call the original handler
          return handler.call(element, e);
        } catch (error) {
          Debug.log('error', `${DEBUG.ERROR_LOG_PREFIX} Error in event handler`, {
            error,
            event,
            elementId: element.id,
            elementType: element.tagName,
            stackTrace: error.stack
          });
        }
      };

      // Store the wrapped handler for removal
      if (!element._eventHandlers) {
        element._eventHandlers = new Map();
      }
      if (!element._eventHandlers.has(event)) {
        element._eventHandlers.set(event, new Map());
      }
      element._eventHandlers.get(event).set(handler, wrappedHandler);

      // Add the event listener
      element.addEventListener(event, wrappedHandler, options);
    } catch (error) {
      Debug.log('error', `${DEBUG.ERROR_LOG_PREFIX} Failed to add event listener`, {
        error,
        event,
        elementId: element.id,
        elementType: element.tagName,
        stackTrace: error.stack
      });
    }
  },

  /**
   * Remove event listener with error handling
   * @param {Element} element - Target element
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  off(element, event, handler) {
    if (!element || typeof handler !== 'function') return;
    
    try {
      // Get the wrapped handler
      const wrappedHandler = element._eventHandlers?.get(event)?.get(handler);
      if (wrappedHandler) {
        element.removeEventListener(event, wrappedHandler);
        element._eventHandlers.get(event).delete(handler);
      } else {
        // Fallback to removing the original handler
        element.removeEventListener(event, handler);
      }
    } catch (error) {
      Debug.log('error', `${DEBUG.ERROR_LOG_PREFIX} Failed to remove event listener`, {
        error,
        event,
        elementId: element.id,
        elementType: element.tagName,
        stackTrace: error.stack
      });
    }
  },

  /**
   * Check if we're in a sandboxed environment
   * @returns {boolean} True if in sandbox
   */
  isSandboxed() {
    return window.origin === 'null' || 
      window.location.origin === 'null' || 
      !window.origin;
  },

  /**
   * Create element with attributes
   * @param {string} tag - Element tag name
   * @param {Object} [attributes] - Element attributes
   * @param {string} [content] - Element content
   * @returns {Element} Created element
   */
  create(tag, attributes = {}, content = '') {
    const element = document.createElement(tag);
    
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'className') {
        element.className = value;
      } else if (key === 'dataset') {
        Object.entries(value).forEach(([dataKey, dataValue]) => {
          element.dataset[dataKey] = dataValue;
        });
      } else {
        element.setAttribute(key, value);
      }
    });
    
    if (content) {
      element.innerHTML = content;
    }
    
    return element;
  },

  /**
   * Check if element is visible
   * @param {Element} element - Element to check
   * @returns {boolean} Is visible
   */
  isVisible(element) {
    if (!element) return false;
    
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0';
  },

  /**
   * Get element's position relative to viewport
   * @param {Element} element - Target element
   * @returns {Object} Position object
   */
  getPosition(element) {
    if (!element) return { top: 0, left: 0, width: 0, height: 0 };
    
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height
    };
  }
};

/**
 * Validation utilities
 */
export const Validation = {
  /**
   * Validate cart data
   * @param {any} cart - Cart object to validate
   * @returns {boolean} Is valid
   */
  validateCart(cart) {
    return TypeCheckers.isCart(cart);
  },

  /**
   * Validate cart item
   * @param {any} item - Cart item to validate
   * @returns {boolean} Is valid
   */
  validateCartItem(item) {
    return TypeCheckers.isCartItem(item);
  },

  /**
   * Validate variant ID
   * @param {any} variantId - Variant ID to validate
   * @returns {boolean} Is valid
   */
  validateVariantId(variantId) {
    return TypeCheckers.isValidVariantId(variantId);
  },

  /**
   * Validate quantity
   * @param {any} quantity - Quantity to validate
   * @returns {boolean} Is valid
   */
  validateQuantity(quantity) {
    return TypeCheckers.isValidQuantity(quantity);
  },

  /**
   * Sanitize HTML content
   * @param {string} html - HTML string to sanitize
   * @returns {string} Sanitized HTML
   */
  sanitizeHTML(html) {
    const div = document.createElement('div');
    div.textContent = html;
    return div.innerHTML;
  },

  /**
   * Validate email address
   * @param {string} email - Email to validate
   * @returns {boolean} Is valid email
   */
  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
};

/**
 * Formatting utilities
 */
export const Format = {
  /**
   * Format money amount
   * @param {number} cents - Amount in cents
   * @param {string} format - Money format string (e.g. "${{amount}}")
   * @param {string} currency - Currency code (optional)
   * @returns {string} Formatted amount
   */
  money(cents, format = null, currency = null) {
    if (!cents || isNaN(cents)) {
      const fallbackAmount = '0.00';
      if (format) {
        return format.replace('{{amount}}', fallbackAmount);
      }
      return fallbackAmount;
    }
    
    const amount = (cents / 100).toFixed(2);
    
    // If format is provided, use it (legacy behavior)
    if (format) {
      return format.replace('{{amount}}', amount);
    }
    
    // Otherwise, use proper currency formatting
    try {
      // Get currency from Shopify context if not provided
      if (!currency) {
        currency = window.Shopify?.currency?.active || 
                  window.Shopify?.shop?.currency || 
                  document.documentElement.getAttribute('data-currency') ||
                  'USD';
      }
      
      // Get locale from Shopify context or use browser default
      const locale = window.Shopify?.locale || 
                    document.documentElement.getAttribute('data-locale') ||
                    navigator.language ||
                    'en-US';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (error) {
      // Fallback to simple format
      return `$${amount}`;
    }
  },

  /**
   * Format number with commas
   * @param {number} num - Number to format
   * @returns {string} Formatted number
   */
  number(num) {
    if (!num || isNaN(num)) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  /**
   * Pluralize word based on count
   * @param {number} count - Count
   * @param {string} singular - Singular form
   * @param {string} [plural] - Plural form (optional)
   * @returns {string} Pluralized word
   */
  pluralize(count, singular, plural = singular + 's') {
    return count === 1 ? singular : plural;
  },

  /**
   * Truncate text to specified length
   * @param {string} text - Text to truncate
   * @param {number} length - Maximum length
   * @param {string} [suffix='...'] - Suffix for truncated text
   * @returns {string} Truncated text
   */
  truncate(text, length, suffix = '...') {
    if (text.length <= length) return text;
    return text.substring(0, length - suffix.length) + suffix;
  },

  /**
   * Convert string to kebab-case
   * @param {string} str - String to convert
   * @returns {string} Kebab-case string
   */
  kebabCase(str) {
    return str
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase();
  }
};

/**
 * Storage utilities
 */
export const Storage = {
  /**
   * Get item from localStorage with error handling
   * @param {string} key - Storage key
   * @param {any} [defaultValue] - Default value if not found
   * @returns {any} Stored value or default
   */
  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`${DEBUG.ERROR_LOG_PREFIX} Failed to get from storage:`, error);
      return defaultValue;
    }
  },

  /**
   * Set item in localStorage with error handling
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @returns {boolean} Success status
   */
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`${DEBUG.ERROR_LOG_PREFIX} Failed to set storage:`, error);
      return false;
    }
  },

  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   * @returns {boolean} Success status
   */
  remove(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`${DEBUG.ERROR_LOG_PREFIX} Failed to remove from storage:`, error);
      return false;
    }
  },

  /**
   * Clear all localStorage items with prefix
   * @param {string} prefix - Key prefix
   */
  clearWithPrefix(prefix) {
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(prefix))
        .forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error(`${DEBUG.ERROR_LOG_PREFIX} Failed to clear storage:`, error);
    }
  }
};

/**
 * URL utilities
 */
export const URL = {
  /**
   * Parse URL parameters
   * @param {string} [url=window.location.href] - URL to parse
   * @returns {Object} Parsed parameters
   */
  parseParams(url = window.location.href) {
    const params = {};
    const urlObj = new window.URL(url);
    
    for (const [key, value] of urlObj.searchParams) {
      params[key] = value;
    }
    
    return params;
  },

  /**
   * Build URL with parameters
   * @param {string} base - Base URL
   * @param {Object} params - Parameters to add
   * @returns {string} Built URL
   */
  build(base, params = {}) {
    const url = new window.URL(base, window.location.origin);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, value);
      }
    });
    
    return url.toString();
  }
};

/**
 * Browser utilities
 */
export const Browser = {
  /**
   * Check if browser supports a feature
   * @param {string} feature - Feature to check
   * @returns {boolean} Is supported
   */
  supports(feature) {
    const features = {
      fetch: typeof fetch !== 'undefined',
      requestIdleCallback: typeof requestIdleCallback !== 'undefined',
      intersectionObserver: typeof IntersectionObserver !== 'undefined',
      localStorage: (() => {
        try {
          localStorage.setItem('test', 'test');
          localStorage.removeItem('test');
          return true;
        } catch {
          return false;
        }
      })()
    };
    
    return features[feature] || false;
  },

  /**
   * Get browser information
   * @returns {Object} Browser info
   */
  getInfo() {
    const ua = navigator.userAgent;
    return {
      isMobile: /Mobi|Android/i.test(ua),
      isIOS: /iPad|iPhone|iPod/.test(ua),
      isAndroid: /Android/.test(ua),
      isSafari: /Safari/.test(ua) && !/Chrome/.test(ua),
      isChrome: /Chrome/.test(ua),
      isFirefox: /Firefox/.test(ua)
    };
  }
};

/**
 * Debug utilities
 */
export const Debug = {
  ERROR_LOG_PREFIX: '[Cart Error]',
  INFO_LOG_PREFIX: '[Cart Info]',
  DEBUG_LOG_PREFIX: '[Cart Debug]',
  WARN_LOG_PREFIX: '[Cart Warn]',

  /**
   * Log message with appropriate level
   * @param {'error'|'info'|'debug'|'warn'} level - Log level
   * @param {string} message - Message to log
   * @param {*} [data] - Additional data to log
   */
  log(level, message, data = null) {
    // Force enable logging for debugging
    const isEnabled = true; // window?.cart?.config?.enableLogging;

    if (!isEnabled && level === 'debug') return;

    const timestamp = new Date().toISOString();
    const prefix = this[`${level.toUpperCase()}_LOG_PREFIX`];

    switch (level) {
      case 'error':
        console.error(`${prefix} ${message}`, data);
        break;
      case 'warn':
        console.warn(`${prefix} ${message}`, data);
        break;
      case 'info':
        console.log(`${prefix} ${message}`, data);
        break;
      case 'debug':
        console.log(`${prefix} ${message}`, data);
        break;
    }

    // Emit debug event for external monitoring
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cart:debug', {
        detail: {
          level,
          message,
          data,
          timestamp
        }
      }));
    }
  },

  /**
   * Create performance mark
   * @param {string} name - Mark name
   */
  mark(name) {
    if (performance.mark) {
      performance.mark(name);
    }
  },

  /**
   * Measure performance between marks
   * @param {string} name - Measure name
   * @param {string} startMark - Start mark name
   * @param {string} endMark - End mark name
   */
  measure(name, startMark, endMark) {
    if (performance.measure) {
      try {
        performance.measure(name, startMark, endMark);
      } catch (error) {
        console.warn(`${DEBUG.WARN_LOG_PREFIX} Failed to measure performance:`, error);
      }
    }
  }
}; 

/**
 * Safe postMessage utility
 */
export const SafePostMessage = {
  /**
   * Safe postMessage utility
   */
  postMessage(targetWindow, message, targetOrigin = '*') {
    try {
      // If we're in a sandbox or the target origin is null, use '*'
      const safeOrigin = DOM.isSandboxed() ? '*' : (targetOrigin || window.location.origin);
      
      Debug.log('info', 'Sending postMessage', {
        message,
        targetOrigin: safeOrigin,
        isSandboxed: DOM.isSandboxed()
      });
      
      targetWindow.postMessage(message, safeOrigin);
    } catch (error) {
      Debug.log('error', 'Failed to send postMessage', {
        error,
        message,
        targetOrigin,
        stackTrace: error.stack
      });
    }
  },

  /**
   * Add postMessage listener with error handling
   * @param {Function} handler - Message handler
   * @param {string} [sourceOrigin] - Expected source origin
   */
  onMessage(handler, sourceOrigin) {
    if (typeof handler !== 'function') return;

    const wrappedHandler = (event) => {
      try {
        // In sandbox, accept all origins
        if (DOM.isSandboxed() || !sourceOrigin || event.origin === sourceOrigin) {
          handler(event);
        } else {
          Debug.log('warn', 'Ignored message from unexpected origin', {
            eventOrigin: event.origin,
            expectedOrigin: sourceOrigin
          });
        }
      } catch (error) {
        Debug.log('error', 'Error in message handler', {
          error,
          eventOrigin: event.origin,
          stackTrace: error.stack
        });
      }
    };

    window.addEventListener('message', wrappedHandler);
    return wrappedHandler; // Store for removal if needed
  }
};

// Export performance monitoring system
export { cartPerformance, CartPerformance };

// Export smart updater system
export { smartUpdater, SmartUpdater }; 